"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type FAQVariant = "default" | "surface";

interface FAQItemProps {
  question: string;
  children: ReactNode;
  variant?: FAQVariant;
}

/**
 * Single FAQ item with expandable answer.
 *
 * This is the ONLY client module in the FAQ pair, and deliberately so.
 * `useState` for the accordion is the sole reason any of this needs to run on
 * the client — so the boundary is drawn here, one level below `<FAQ>`, which
 * derives the FAQPage JSON-LD from these elements while still on the server.
 *
 * Do not add `"use client"` to `FAQ.tsx` to co-locate them again: that puts the
 * boundary ABOVE the schema derivation, and MDX answers then reach it as
 * serialized chunk references instead of readable elements. See the module
 * comment in `FAQ.tsx` for what that cost.
 */
export function FAQItem({ question, children, variant = "default" }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (variant === "surface") {
    return (
      <div className="bg-surface-container rounded-xl overflow-hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-8 py-6 text-left flex justify-between items-center group"
        >
          <span className="min-w-0 text-lg font-bold text-primary hyphens-auto break-words">
            {question}
          </span>
          <ChevronDown
            className={cn(
              "w-5 h-5 flex-shrink-0 text-secondary transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>
        <div
          className={cn(
            "overflow-hidden transition-all duration-200",
            isOpen ? "max-h-[1000px]" : "max-h-0"
          )}
        >
          <div className="px-8 pb-6 text-on-surface-variant font-light border-t border-black/5 pt-4 hyphens-auto break-words">
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-primary/10 rounded-[14px] px-5 py-4 sm:px-6 sm:py-5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-4 text-left group"
      >
        <h3 className="text-[17px] font-extrabold text-primary group-hover:text-secondary transition-colors">
          {question}
        </h3>
        <ChevronDown
          className={cn(
            "w-5 h-5 flex-shrink-0 text-secondary transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          isOpen ? "max-h-[1000px]" : "max-h-0"
        )}
      >
        <div className="pt-2.5 text-[15px] leading-[1.6] text-on-surface-variant space-y-3 hyphens-auto break-words">
          {children}
        </div>
      </div>
    </div>
  );
}
